import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import queryString from 'query-string';
import { connect, useDispatch } from 'react-redux';
import GeneralError from '../../components/GeneralError';
import LoaderBar from '../../components/loader-bar/LoaderBar';
import ProjectCardList from '../../project/project-card-list/ProjectCardList';
import OrganizationHeader from '../organization-header/OrganizationHeader';
import SchoolProjectsForOrganizationSearch from '../school-project-search/SchoolProjectsForOrganizationSearch';
import OrganizationDescription from './organization-description/OrganizationDescription';
import { trackPageView } from '../../support-and-tracking/analytics-actions';
import OrganizationCtaTemplates from './organization-cta-templates/OrganizationCtaTemplates';
import OrganizationContext from '../organization-context';
import Main from '../../frame/main/Main';
import ActivateEmailBanner from '../../user/activate-email-banner/ActivateEmailBanner';
import Banner from '../../components/banner/Banner';
import { decrypt, logoutBackend } from '../../user/user-actions';
import ButtonText from '../../components/button/button-text/ButtonText';
import GenericHelmet from '../../project/generic-helmet/GenericHelmet';
import CreateProjectContext from '../../context/create-project-context';
import { fetchActiveTemplateCausesForOrganization, fetchOrganization } from '../organization-actions';
import OrganizationAdmin from '../organization-control/organization-admin/OrganizationAdmin';
import { userIsMemberOfOrganization } from '../authenticated-organization-helper';
import ProjectCardContext from '../../context/project-card-context';
import withRouter from '../../components/hooks/withRouter';
import RoundedButtonPrimary from '../../components/button/rounded-button-primary/RoundedButtonPrimary';
import RoundedButtonSecondary from '../../components/button/rounded-button-secondary/RoundedButtonSecondary';
import { ChangePaletteModal } from '../organization-theme/ChangePaletteModal';
import AdminHeader from '../../admin/AdminHeader';
import Header from '../../frame/header/Header';
import { WHITE_ORGANIZATION_COLOR } from '../theme/OrganizationTheme';
import OrganizationStartProject from './organization-start-project/OrganizationStartProject';
import { KINDERGARTEN_TYPE, SCHOOL_TYPE } from '../../skolelop/school-model';
import OrganizationRecurring from '../organization-recurring/OrganizationRecurring';
import useQueryString from '../../components/hooks/useQueryString';
import Footer from '../../frame/footer/Footer';
import styles from './organization-show.module.less';
import { getPublicProjectIdsForOrganization } from '../../project/project-actions';
import { Heading2, Heading4 } from '@sb1/ffe-core-react';

const OrganizationShow = (props) => {
    const { isFetching, organization, user, unauthorized, inactive, emailData, userIsOrganizationMember } = props;

    const [editingOrganization, setEditingOrganization] = useState(false);

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const params = useParams();
    const [{ edit }] = useQueryString();
    const [predefinedEmail, setPredefinedEmail] = useState();

    const decryptEmailData = async (emailData, dispatch) => {
        try {
            const data = emailData ? await dispatch(decrypt(emailData)) : null;
            setPredefinedEmail(data?.body?.decrypted);
        } catch (e) {}
    };

    useEffect(() => {
        trackPageView({ urlAsEvent: 'org', properties: { 'organization-id': params.organization_id } });
    }, []);

    useEffect(() => {
        decryptEmailData(emailData, dispatch);
    }, []);

    useEffect(() => {
        if (edit === 'true') {
            setEditingOrganization(true);
        }
    }, [edit]);

    const finishedEditingOrganization = async () => {
        await dispatch(fetchOrganization(organization.id, true));
        setEditingOrganization(false);
    };

    const logout = async (e, dispatch, emailData, orgId) => {
        e.preventDefault();
        const emailDataUrlPart = emailData ? `?emailData=${encodeURIComponent(emailData)}` : '';
        try {
            await dispatch(logoutBackend());
            navigate(`/org/${orgId}/edit${emailDataUrlPart}`, { replace: true });
        } catch (e) {}
    };

    if (isFetching && !organization) {
        return (
            <Main>
                <LoaderBar subText="Henter spleiser" />
            </Main>
        );
    } else if (!organization) {
        return (
            <Main>
                <GeneralError error="Fant ingen organisasjon" />
            </Main>
        );
    }
    const { id, name, projects } = organization;
    const cardOptions = {
        owner: true,
        receiver: false,
    };

    const displayCta = !organization.profile?.hideCreateProjectButton && !organization.causes;

    const displayCreateByTemplateCta =
        userIsOrganizationMember || !!organization.causes || !!organization.profile?.organizationPageLinks;

    const unauthorizedText = `Du har ikke tilgang til å endre denne organisasjonen.${
        predefinedEmail
            ? ` Logg inn eller registrer deg med ${predefinedEmail} for å få tilgang til organisasjonen din.`
            : ''
    }`;

    const createProjectParams = organization.profile?.createProjectParams || `?organizationId=${id}`;
    return (
        <OrganizationContext.Provider
            value={{
                id,
                themeColor: organization?.profile?.themeColor,
                campaignTitle: organization?.profile?.campaignTitle,
                industrial_code: organization?.industrial_code,
                name: organization?.name,
            }}
        >
            <CreateProjectContext.Provider value={{ createProjectParams }}>
                <GenericHelmet
                    title={name}
                    description={`Samle inn penger til ${name} gjennom å starte eller gi til en innsamling`}
                    imageUrl={organization.description_image}
                />
                <AdminHeader />
                {!editingOrganization && <Header showSb1Logo />}
                {editingOrganization && (
                    <OrganizationAdmin organizationId={organization.id} editMode={true}>
                        <ChangePaletteModal />
                        <Heading4 className={styles.editTitle} />
                        <RoundedButtonSecondary onClick={finishedEditingOrganization}>
                            <span className={styles.closeEdit} />
                        </RoundedButtonSecondary>
                    </OrganizationAdmin>
                )}

                {!editingOrganization && (
                    <OrganizationAdmin organizationId={organization.id} editMode={false}>
                        <RoundedButtonPrimary onClick={() => setEditingOrganization(true)}>
                            <i className={'fa-regular fa-pen-to-square'} />
                            <span className={styles.adminLinkText}>Rediger siden</span>
                        </RoundedButtonPrimary>
                    </OrganizationAdmin>
                )}

                {unauthorized && (
                    <Banner bannerText={unauthorizedText}>
                        <ButtonText onClick={(e) => logout(e, dispatch, emailData, organization.id)}>
                            Logg ut
                        </ButtonText>
                    </Banner>
                )}

                {inactive && !user.activated && <ActivateEmailBanner />}

                <div className={styles.organizationContainer}>
                    <OrganizationHeader
                        organization={organization}
                        displayCta={displayCta}
                        editable={editingOrganization}
                    />

                    {displayCreateByTemplateCta && (
                        <OrganizationCtaTemplates
                            templateCauses={organization.causes}
                            templatesText={organization.profile?.projectTemplatesText}
                            pageLinks={organization.profile?.organizationPageLinks}
                            userIsOrganizationMember={userIsOrganizationMember}
                            organizationId={organization.id}
                            editable={editingOrganization}
                        />
                    )}

                    {organization.profile?.showSchoolSearch && (
                        <SchoolProjectsForOrganizationSearch
                            organizationId={organization.id}
                            organizationName={name}
                            type={SCHOOL_TYPE}
                        />
                    )}
                    {organization.profile?.showKindergartenSearch && (
                        <SchoolProjectsForOrganizationSearch
                            organizationId={organization.id}
                            organizationName={name}
                            type={KINDERGARTEN_TYPE}
                        />
                    )}
                    <OrganizationStartProject />
                    {organization?.profile?.showRecurringPaymentRecruitmentOnOrgPage && (
                        <OrganizationRecurring organization={organization} editable={editingOrganization} />
                    )}

                    <OrganizationDescription
                        organization={organization}
                        title={`Om organisasjonen`}
                        editable={editingOrganization}
                    />
                    {projects?.length > 0 && (
                        <section className={styles.projectListContainer}>
                            <div className={styles.projectList}>
                                {projects?.length > 0 && (
                                    <>
                                        <Heading2
                                            lookLike={3}
                                            className={[styles.projectSelectionTitle, WHITE_ORGANIZATION_COLOR].join(
                                                ' '
                                            )}
                                        >
                                            Innsamlinger
                                        </Heading2>

                                        <ProjectCardContext.Provider value={cardOptions}>
                                            <ProjectCardList
                                                projectIds={projects}
                                                limit={8}
                                                actionInformation="Organization - active"
                                                cardTitleHeadingLevel={3}
                                            />
                                        </ProjectCardContext.Provider>
                                    </>
                                )}
                            </div>
                        </section>
                    )}
                </div>
                {!editingOrganization && <Footer />}
            </CreateProjectContext.Provider>
        </OrganizationContext.Provider>
    );
};

export const loader = async ({ dispatch, params }) => {
    const { organization_id } = params;
    if (organization_id) {
        await Promise.all([
            dispatch(getPublicProjectIdsForOrganization({ organizationId: organization_id })),
            dispatch(fetchOrganization(organization_id)),
            dispatch(fetchActiveTemplateCausesForOrganization({ organizationId: organization_id })),
        ]);
    }
    return null;
};

OrganizationShow.propTypes = {
    dispatch: PropTypes.func.isRequired,
    isFetching: PropTypes.bool.isRequired,
    organization: PropTypes.object,
    emailData: PropTypes.string,
    predefinedEmail: PropTypes.string,
};

const mapStateToProps = (state, props) => {
    const user = state.user;
    const query = queryString.parse(props.location.search);
    const organizationId = parseInt(props.params.organization_id, 10);
    const unauthorized = !!query?.unauthorized;
    const inactive = !!query?.inactive;
    const emailData = query?.emailData;
    const organization = state.organizations.organizations.find((o) => o.id === organizationId);
    const userIsOrganizationMember = userIsMemberOfOrganization({ user, organizationId });

    return {
        user,
        unauthorized,
        inactive,
        emailData,
        organization,
        isFetching: state.organizations.isFetching,
        userIsOrganizationMember,
    };
};

export default withRouter(connect(mapStateToProps)(OrganizationShow));
